<template>
  <main id="products-list" class="site-main">
    <div class="grid-container">
      <div class="page-header grid-x grid-padding-x align-middle">
        <div class="cell auto">
          <h1 class="page-header__title">Products</h1>

          <div class="text-right">
            <span class="icon-action" @click="exportProducts">
              <i class="far fa-arrow-to-bottom"></i> <span>Export</span>
            </span>
          </div>
        </div>
      </div>

      <div class="panel no-padding">
        <searchBar placeholder="Search products" @sort="sort"
                   :value="searchValue" @search="search"  :dateFilter="true"
                   @dateFilter="dateFilter"></searchBar>

        <div :class="['loading-area', loading ? 'loading' : '']">
          <div :class="['loading-area__content', loading ? 'loading' : '']">
            <pagination :prev="prev" :next="next" :start="start" :end="end"
                        :total="total" @paginate="paginate"></pagination>

            <div style="overflow: scroll;">
              <tableListV2 :columns="visibleColumns" :data="tableData"
                         :currentSort="tableSort"
                         @sort="orderBy" @callback="callback"
                         style="min-width: 100%;"
                         @post="go"></tableListV2>
            </div>

            <pagination :prev="prev" :next="next" :start="start" :end="end"
                        :total="total" @paginate="paginate"></pagination>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import client from '@/mixins/client'
import paginationUtils from '@/mixins/paginationUtils'
import tableListV2 from '@/globals/tableListV2'
import { PREFIX } from '@/localSettings'
import moment from 'moment'

export default {
  mixins: [client, paginationUtils],
  components: {
    tableListV2
  },
  data: function () {
    return {
      action: '/products/?expand=variants&vendor=' + this.$store.state.vendors[this.$store.state.currentVendor].id,
      sort: 'title',
      tableSort: 'title',
      domain: this.$store.state.portalSettings.myshopify_domain,
      vendorId: this.$store.state.vendors[this.$store.state.currentVendor].id,
      columns: [],
      tableData: [],
      tableHeader: { currentSort: 'title' },
      blockedDomains: [
        'qualityfood.myshopify.com',
        'physicians-longevity.myshopify.com',
        'the-harmonic-au.myshopify.com',
        'kina-11.myshopify.com',
        'kingsroy-gallery.myshopify.com',
        'curated-cubs.myshopify.com',
        'dantes-luxury-lifestyle.myshopify.com',
        'stylecolab-preloved.myshopify.com',
        'lisco-ny.myshopify.com',
        'starlitdreamer.myshopify.com',
        'renee-taylor-gallery.myshopify.com',
        '905abc-15.myshopify.com'
      ]
    }
  },
  computed: {
    visibleColumns () {
      return this.columns.filter(c => !c.hidden)
    },
    isBlockedDomain () {
      return this.blockedDomains.includes(this.domain)
    }
  },
  methods: {
    orderBy (sort) {
      this.sort = sort
      this.tableSort = sort
      this.page = 1
      this.addParamsToLocation({
        s: this.searchValue,
        sort: this.sort,
        page: this.page
      })
      this.getData()
    },
    buildColumns () {
      const domain = this.domain
      const metas = this.$store.state.metas
      const isBlocked = this.isBlockedDomain

      // For qualityfood: completely custom header
      if (domain === 'qualityfood.myshopify.com') {
        return [
          { key: 'product', label: 'Product', class: 'full', sort: 'title' },
          { key: 'barcode', label: 'Barcode', class: 'medium' },
          { key: 'price', label: 'Price', class: 'medium' },
          { key: 'inventory', label: 'Inventory', class: 'xlarge' },
          { key: 'status', label: 'Status', class: 'medium' },
          { key: 'created', label: 'Created', class: 'medium', sort: 'created_at' },
          { key: 'view', label: 'View', class: 'medium' }
        ]
      }

      // For non-blocked domains: simplified header
      if (!isBlocked) {
        let cols = [
          { key: 'product', label: 'Product', class: 'full', sort: 'title' },
          { key: 'barcode', label: 'Barcode', class: 'medium' },
          { key: 'price', label: 'Price', class: 'medium', hidden: metas.vendor_field_sell_price.meta_value === false },
          { key: 'inventory', label: 'Inventory', class: 'xlarge' },
          { key: 'paid', label: 'Paid', class: 'small' },
          { key: 'commission', label: 'Commission', class: 'medium', hidden: !metas.vendor_field_commissions.meta_value },
          { key: 'created', label: 'Created', class: 'medium', sort: 'created_at' },
          { key: 'view', label: 'View', class: 'medium' }
        ]
        return cols
      }

      // Blocked domains: full column set with conditional visibility
      const isKinaOrKingsroy = (domain === 'kina-11.myshopify.com' || domain === 'kingsroy-gallery.myshopify.com')
      const showCommission = (() => {
        if (domain === 'curated-cubs.myshopify.com' || domain === 'the-thrifting-den.myshopify.com') return true
        if (isKinaOrKingsroy && !metas.vendor_field_commissions.meta_value) return false
        return !!metas.vendor_field_commissions.meta_value
      })()

      return [
        { key: 'product', label: 'Product', class: 'full', sort: 'title' },
        { key: 'sku', label: 'SKU', class: 'large', hidden: !isKinaOrKingsroy },
        { key: 'barcode', label: 'Barcode', class: 'medium', hidden: domain === 'renee-taylor-gallery.myshopify.com' || isKinaOrKingsroy },
        { key: 'price', label: 'Price', class: 'large', hidden: metas.vendor_field_sell_price.meta_value === false },
        { key: 'cost', label: 'Item Cost', hidden: domain !== 'curated-cubs.myshopify.com' },
        { key: 'inventory', label: 'Inventory', class: 'xlarge', hidden: domain === 'leivitorloveit.myshopify.com' || domain === 'mostlymusic.myshopify.com' },
        { key: 'sold', label: 'Sold', class: 'small', hidden: domain !== 'stylecolab-preloved.myshopify.com' },
        { key: 'paid', label: 'Paid', class: 'small', hidden: domain !== 'dantes-luxury-lifestyle.myshopify.com' },
        { key: 'available', label: 'Available', class: 'large', hidden: domain !== 'stylecolab-preloved.myshopify.com' },
        { key: 'commission', label: 'Commission', class: 'medium', hidden: !showCommission },
        { key: 'earning', label: 'Earning Potential', class: 'medium', hidden: domain !== '905abc-15.myshopify.com' },
        { key: 'status', label: 'Status', class: 'medium', hidden: domain !== 'qualityfood.myshopify.com' },
        { key: 'created', label: 'Created', class: 'large', sort: 'created_at' },
        { key: 'view', label: 'View', class: 'medium', hidden: domain === 'starlitdreamer.myshopify.com' }
      ]
    },
    exportProducts () {
      let exportAction = '/products/?expand=variants&vendor=' + this.vendorId
      if (this.domain === 'deathwish-inc.myshopify.com' || this.domain === 'fd7208-a5.myshopify.com' || this.domain === 'the-harmonic-au.myshopify.com') {
        exportAction = '/products/variants/?vendor=' + this.vendorId
      }

      this.request('get', exportAction, {
        format: 'csv'
      }).then((response) => {
        let downloadLink = document.createElement('a')
        downloadLink.href = response
        downloadLink.target = '_blank'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
        this.loading = false
      })
    },
    dateFilter (startDate, endDate) {
      this.params = {}
      if (startDate.length > 0) {
        this.params['start_date'] = startDate
      } else {
        delete this.params['start_date']
      }

      if (endDate.length > 0) {
        this.params['end_date'] = endDate
      } else {
        delete this.params['end_date']
      }

      if (this.params['start_date'] || this.params['end_date']) {
        this.action = '/products/sold/?expand=variants&vendor=' + this.vendorId
      } else {
        this.action = '/products/?expand=variants&vendor=' + this.vendorId
      }

      this.getData()
    },
    go: function (id) {
      this.$router.push(PREFIX + '/products/' + id)
    },
    viewProduct (handle) {
      let link = document.createElement('a')
      link.href = 'https://' + this.$store.state.portalSettings.domain + '/products/' + handle + '/'
      link.target = '_blank'
      link.click()
    },
    buildTable () {
      for (let i = 0; i < this.data.length; i++) {
        let item = this.data[i]

        // Handle lisco-ny variant expansion
        if (this.domain === 'lisco-ny.myshopify.com') {
          item.image = item.product.image
          item.title = item.product.title + '<br>' + item.title
          item.product_type = item.product.product_type
        }

        // Build product cell with image
        let productHtml = '<div class="grid-x align-middle"><div class="cell shrink">' +
          '<div class="table-list__image">'

        if (!this.isEmpty(item.image)) {
          let ext = item.image.split('.').pop()
          item.image = item.image.replace('.' + ext, '_100x100.' + ext)
          productHtml += '<div class="table-list__image__inner">'
          productHtml += '<img src="' + item.image + '">'
          productHtml += '</div>'
        } else {
          productHtml += '<div class="table-list__image__missing"><i class="fas fa-image"></i></div>'
        }

        productHtml += '</div></div><div class="cell auto">' + item.title + '</div></div>'

        // Calculate variant stats
        let variantCount = 0
        let inventoryCount = 0
        let sold = 0
        let paid = 0

        if (this.domain !== 'lisco-ny.myshopify.com') {
          for (let j = 0; j < item.variants.length; j++) {
            variantCount++
            inventoryCount += item.variants[j].inventory_quantity
            sold += item.variants[j].sold
            paid += item.variants[j].paid
          }
        } else {
          inventoryCount = item.inventory_quantity
          variantCount = 1
        }

        let inventory = inventoryCount + ' in stock'
        if (variantCount > 1) {
          inventory += ' for ' + variantCount + ' variants'
        }

        // Calculate price
        let priceValue = ''
        if (this.$store.state.metas.vendor_field_sell_price.meta_value !== false && item.variants) {
          let lowestPrice = 9999999999
          for (let j = 0; j < item.variants.length; j++) {
            if (item.variants[j].price < lowestPrice) {
              lowestPrice = item.variants[j].price
            }
          }
          priceValue = this.formatMoney(lowestPrice)
          if (item.variants.length > 1) {
            priceValue = 'starting at ' + priceValue
          }
        }

        // Calculate SKU for kina/kingsroy
        let skuValue = ''
        if (item.variants) {
          if (item.variants.length > 1) {
            skuValue = 'multiple'
          } else {
            skuValue = item.variants[0].sku
          }
        }

        // Commission value
        let commissionValue = ''
        if (this.domain === 'curated-cubs.myshopify.com' || this.domain === 'the-thrifting-den.myshopify.com') {
          commissionValue = item.variants && item.variants[0] ? this.formatMoney(item.variants[0].cost_per_item) : ''
        } else if (this.$store.state.metas.vendor_field_commissions.meta_value) {
          commissionValue = this.formatMoney(item.commission)
        }

        // Cost per item for curated-cubs
        let costValue = ''
        if (item.variants && item.variants[0] && item.variants[0].cost_per_item) {
          costValue = this.formatMoney(item.variants[0].cost_per_item)
        } else {
          costValue = this.formatMoney(0)
        }

        // Available col for stylecolab
        let availableValue = ''
        if (this.domain === 'stylecolab-preloved.myshopify.com') {
          availableValue = 'Start: ' + item.start_date + '<br>End: ' + item.end_date
        }

        // View button
        let viewValue = '<button class="button">View</button>'

        this.tableData.push({
          post: item.id,
          data: {
            product: { value: productHtml, class: 'full' },
            sku: { value: skuValue, class: 'large' },
            barcode: { value: item.variants && item.variants[0] ? item.variants[0].barcode : '', class: 'medium' },
            price: { value: priceValue, class: 'large', span: 'fill' },
            cost: { value: costValue, class: 'medium' },
            inventory: { value: inventory, class: 'xlarge' },
            sold: { value: sold, class: 'small' },
            paid: { value: paid, class: 'small' },
            available: { value: availableValue, class: 'large' },
            commission: { value: commissionValue, class: 'medium', span: 'focus-text' },
            earning: { value: item.get_earning_potential, class: 'medium' },
            status: { value: item.status, class: 'medium' },
            created: { value: moment(item.created_at).format('MMM D, YYYY'), class: 'large' },
            view: { value: viewValue, class: 'medium', callback: 'viewProduct', callbackArgs: [item.handle] }
          }
        })
      }
    }
  },
  created () {
    // Set initial action based on domain
    if (this.domain === 'lisco-ny.myshopify.com') {
      this.action = '/products/variants/?vendor=' + this.vendorId
    } else if (this.domain === 'physicians-longevity.myshopify.com' || this.domain === 'the-harmonic-au.myshopify.com') {
      this.action = '/products/?expand=variants'
    }

    // Build columns based on domain and settings
    this.columns = this.buildColumns()

    this.getData()
  }
}
</script>

<style lang="scss">
@import '../../styles/mixins';
@import '../../styles/variables';
</style>



// WEBPACK FOOTER //
// src/pages/vendorPortal/productsV2.vue